import React, { useState, useEffect } from 'react';
import { useAuthProvider } from 'react-admin';
import { useMutation } from '@tanstack/react-query';
import {
    Alert, Typography, Button, CircularProgress, Modal, Box,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Stepper, Step, StepLabel, StepContent,
} from '@mui/material';
import { FileDownload, Close, Extension } from '@mui/icons-material';
import extractFileImg from './media/extract-file.png';
import loadUnpackedImg from './media/load_unpacked.png';
import loginExtensionImg from './media/login_extension.png';

const R2PrefixLength = 'https://r2.devsoft.vn/'.length;
const apiUrl = process.env.REACT_APP_API_URL;

const MARKER_ID = '__song_admin_ext__';
const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 4;

/** Minimum build ID required — update this when a new extension release is needed. */
const REQUIRED_VERSION = 78514;

export interface ExtensionStatus {
    /** null while still detecting */
    detected: boolean | null;
    /** The version string from the marker, or null */
    version: string | null;
    /** true when detected but version is below REQUIRED_VERSION */
    outdated: boolean;
}

function isVersionOutdated (version: string | null): boolean {
    if (!version) return false;
    if (version === 'local') return false; // local dev builds always pass
    const num = parseInt(version, 10);
    return !isNaN(num) && num < REQUIRED_VERSION;
}

/** Poll the DOM for the extension's marker element and read its version. */
export function useExtensionDetected (): ExtensionStatus {
    const [status, setStatus] = useState<ExtensionStatus>({ detected: null, version: null, outdated: false });

    useEffect(() => {
        const read = (): ExtensionStatus | null => {
            const el = document.getElementById(MARKER_ID);
            if (!el) return null;
            const version = el.getAttribute('data-version') || null;
            return { detected: true, version, outdated: isVersionOutdated(version) };
        };

        // Immediate check
        const immediate = read();
        if (immediate) {
            setStatus(immediate);
            return;
        }

        let polls = 0;
        let timerId: number;
        const check = () => {
            const result = read();
            if (result) {
                setStatus(result);
                return;
            }
            polls++;
            if (polls >= MAX_POLLS) {
                setStatus({ detected: false, version: null, outdated: false });
                return;
            }
            timerId = window.setTimeout(check, POLL_INTERVAL_MS);
        };
        timerId = window.setTimeout(check, POLL_INTERVAL_MS);
        return () => clearTimeout(timerId);
    }, []);

    return status;
}

async function downloadFromR2 (authProvider: any, r2Url: string): Promise<Blob> {
    const jwt = authProvider.getJWT();
    const r2Resource = r2Url.slice(R2PrefixLength);
    const response = await fetch(`${apiUrl}/r2/${r2Resource}`, {
        headers: { authorization: `Bearer ${jwt}` },
        method: 'GET',
    });
    return response.blob();
}

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const codeStyle: React.CSSProperties = {
    fontFamily: 'monospace',
    backgroundColor: 'rgba(127,127,127,0.15)',
    padding: '2px 6px',
    borderRadius: 3,
    fontSize: '0.9em',
};

const ExtensionSetup: React.FC<{ outdated?: boolean; version?: string | null; onClose?: () => void }> = ({ outdated, version, onClose }) => {
    const [blob, setBlob] = useState<Blob | null>(null);
    const r2Url = `https://r2.devsoft.vn/SongPublic/AdminExtension.Chrome.${REQUIRED_VERSION}.zip`;
    const authProvider = useAuthProvider();
    const { mutate, isPending, isSuccess } = useMutation({
        mutationFn: () => downloadFromR2(authProvider, r2Url).then(setBlob),
    });

    const title = outdated ? 'Extension Update Required' : 'Extension Required';
    const severity: 'info' | 'warning' = outdated ? 'info' : 'warning';
    const alertMessage = outdated
        ? `Your extension (build ${version}) is outdated. Please download the latest version (${REQUIRED_VERSION}) and reload it.`
        : 'The Additional Listings feature requires the Song Admin Chrome Extension to collect seat data from ticketing sites.';

    return (
        <Dialog open maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Extension color="warning" />
                {title}
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity={severity} sx={{ mb: 2 }}>{alertMessage}</Alert>

                <Stepper orientation="vertical">
                    {/* Step 1: Download */}
                    <Step active>
                        <StepLabel>Download the extension</StepLabel>
                        <StepContent>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Click the button below to download the Chrome extension zip file.
                            </Typography>
                            <Button
                                startIcon={isPending ? <CircularProgress size={18} /> : <FileDownload />}
                                variant="outlined"
                                onClick={() => mutate()}
                                disabled={isPending || isSuccess}
                            >
                                {isSuccess ? 'Download Complete' : isPending ? 'Downloading…' : 'Download Extension (.zip)'}
                            </Button>
                        </StepContent>
                    </Step>

                    {/* Step 2: Extract */}
                    <Step active>
                        <StepLabel>Extract the zip file</StepLabel>
                        <StepContent>
                            <Typography variant="body2">
                                Extract the downloaded zip to a permanent folder
                                (e.g. <span style={codeStyle}>C:\extensions\song-admin</span>).
                                Do not delete this folder — Chrome needs it to stay in place.
                            </Typography>
                            <img src={extractFileImg} alt="Extract the zip file" style={{ maxWidth: '100%', height: 'auto', marginTop: 8, borderRadius: 4 }} />
                        </StepContent>
                    </Step>

                    {/* Step 3: Enable Developer Mode */}
                    <Step active>
                        <StepLabel>Enable Developer Mode in Chrome</StepLabel>
                        <StepContent>
                            <ol style={{ margin: 0, paddingLeft: 20 }}>
                                <li>
                                    <Typography variant="body2">
                                        Open Chrome and navigate to{' '}
                                        <span style={codeStyle}>chrome://extensions</span>.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        In the top-right corner, toggle <b>Developer mode</b> to <b>ON</b>.
                                    </Typography>
                                </li>
                            </ol>
                        </StepContent>
                    </Step>

                    {/* Step 4: Load Unpacked */}
                    <Step active>
                        <StepLabel>Load the extension</StepLabel>
                        <StepContent>
                            <ol style={{ margin: 0, paddingLeft: 20 }}>
                                <li>
                                    <Typography variant="body2">
                                        Click the <b>Load unpacked</b> button in the top-left area of the extensions page.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        Browse to the extracted folder and select it.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        <b>Song Admin Extension</b> should now appear in the installed extensions list.
                                    </Typography>
                                </li>
                            </ol>
                            <img src={loadUnpackedImg} alt="Load unpacked extension" style={{ maxWidth: '100%', height: 'auto', marginTop: 8, borderRadius: 4 }} />
                        </StepContent>
                    </Step>

                    {/* Step 5: Authenticate */}
                    <Step active>
                        <StepLabel>Authenticate the extension</StepLabel>
                        <StepContent>
                            <Typography variant="body2">
                                1. Click on the extension's icon in the Chrome toolbar (it may be hidden under the puzzle piece icon).
                                <br />
                                2. In the popup, click the <b>Login</b> button to authenticate with your Song Admin account.
                                <br />
                                3. Once logged in, the extension will automatically start collecting data when you visit supported ticketing sites.
                            </Typography>
                            <img src={loginExtensionImg} alt="Extension login indicator" style={{ maxWidth: '100%', height: 'auto', marginTop: 8, borderRadius: 4 }} />
                        </StepContent>
                    </Step>
                </Stepper>
            </DialogContent>
            <DialogActions>
                {onClose && (
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                )}
                <Button variant="contained" onClick={() => window.location.reload()}>
                    Refresh Page
                </Button>
            </DialogActions>

            {/* Download save modal */}
            {!!blob && (
                <Modal open aria-labelledby="ext-download-title">
                    <Box sx={modalStyle}>
                        <Typography id="ext-download-title" variant="h6">Save the download</Typography>
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                                startIcon={<FileDownload />}
                                variant="outlined"
                                href={URL.createObjectURL(blob)}
                                download={r2Url.split('/').pop()}
                            >
                                Save
                            </Button>
                            <Button
                                startIcon={<Close />}
                                variant="outlined"
                                color="warning"
                                onClick={() => setBlob(null)}
                            >
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            )}
        </Dialog>
    );
};

export default ExtensionSetup;
